import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import Logo from "@/components/Logo";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [kvkk, setKvkk] = useState(false);
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!fullName.trim()) {
      toast.error(t("auth.register.errEmptyName"));
      return;
    }
    if (password.length < 8) {
      toast.error(t("auth.register.errShortPassword"));
      return;
    }
    if (password !== passwordConfirm) {
      toast.error(t("auth.register.errPasswordMismatch"));
      return;
    }
    if (!kvkk) {
      toast.error(t("auth.register.errKvkk"));
      return;
    }
    if (!terms) {
      toast.error(t("auth.register.errTerms"));
      return;
    }
    setLoading(true);
    try {
      await register({
        full_name: fullName.trim(),
        email: email.trim(),
        password,
        kvkk_acknowledged: true,
        terms_accepted: true,
        marketing_consent: marketing,
        consent_lang: lang,
      });
      toast.success(t("auth.register.success"));
      navigate("/app/onboarding");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      let msg;
      if (status === 400) msg = detail || t("auth.register.err400");
      else if (status >= 500) msg = t("auth.register.err500");
      else if (!err?.response) msg = t("auth.register.errNetwork");
      else msg = detail || t("auth.register.errDefault");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const homeHref = lang === "en" ? "/" : "/tr";
  const emailPh = lang === "en" ? "you@yourbusiness.com" : "ornek@isletmem.com";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 relative">
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute top-6 left-6"><Link to={homeHref}><Logo /></Link></div>
      <div className="w-full max-w-md relative z-10">
        <div className="glass-strong rounded-3xl p-8">
          <h1 className="font-display text-3xl font-bold text-white mb-2">{t("auth.register.title")}</h1>
          <p className="text-slate-400 text-sm mb-8">{t("auth.register.subtitle")}</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <Field label={t("auth.register.fullName")}>
              <input
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="reginput"
                placeholder={t("auth.register.fullNamePh")}
                data-testid="register-name"
              />
            </Field>
            <Field label={`${t("auth.email")} *`}>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="reginput"
                placeholder={emailPh}
                data-testid="register-email"
              />
            </Field>
            <Field label={t("auth.register.password")}>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="reginput"
                placeholder={t("auth.register.passwordPh")}
                data-testid="register-password"
              />
            </Field>
            <Field label={t("auth.register.passwordConfirm")}>
              <input
                type="password"
                required
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="reginput"
                placeholder={t("auth.register.passwordConfirmPh")}
                data-testid="register-password-confirm"
              />
            </Field>

            <div className="space-y-2.5 pt-2 border-t border-white/5">
              <Consent checked={kvkk} onChange={setKvkk} testid="register-kvkk" required>
                <a href="/kvkk-aydinlatma-metni" target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300">
                  {lang === "en" ? "KVKK notice" : "KVKK Aydınlatma Metni"}
                </a>
                {lang === "en" ? " — I have read and understood it." : "'ni okudum ve anladım."}
              </Consent>
              <Consent checked={terms} onChange={setTerms} testid="register-terms" required>
                <a href="/kullanim-kosullari" target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300">
                  {lang === "en" ? "Terms of Service" : "Kullanım Koşulları"}
                </a>
                {lang === "en" ? " — I have read and accept them." : "'nı okudum ve onaylıyorum."}
              </Consent>
              <Consent checked={marketing} onChange={setMarketing} testid="register-marketing">
                {t("auth.register.marketing")}{" "}
                <a href="/acik-riza-metni" target="_blank" rel="noreferrer" className="text-cyan-400 hover:text-cyan-300">
                  {lang === "en" ? "Explicit consent" : "Açık Rıza Metni"}
                </a>
              </Consent>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="neon-btn w-full text-white font-semibold rounded-xl py-3.5 flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="register-submit"
            >
              {loading && <Loader2 size={16} className="animate-spin" />}
              {t("auth.register.submit")}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-400">
            {t("auth.register.haveAccount")}{" "}
            <Link to="/giris" className="text-cyan-400 hover:text-cyan-300" data-testid="goto-login">
              {t("auth.register.goLogin")}
            </Link>
          </div>
        </div>
      </div>

      <style>{`
        .reginput {
          margin-top: 0.5rem;
          width: 100%;
          background: rgba(0,0,0,0.4);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 0.5rem;
          padding: 0.75rem 1rem;
          color: #fff;
        }
        .reginput:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 1px #3b82f6; }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-slate-400 uppercase tracking-wider">{label}</label>
      {children}
    </div>
  );
}

function Consent({ checked, onChange, children, required, testid }) {
  return (
    <label
      className="flex items-start gap-2.5 text-xs text-slate-300 leading-relaxed cursor-pointer select-none"
      data-testid={`${testid}-row`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-black/40 accent-cyan-500 shrink-0"
        data-testid={testid}
      />
      <span>
        {children}
        {required && <span className="text-cyan-400 ml-1">*</span>}
      </span>
    </label>
  );
}
